import {
  getHours,
  getMinutes,
  isAfter,
  isBefore,
  isEqual,
  isSameSecond,
  RoundingMethod,
  roundToNearestMinutes,
  setSeconds,
} from "date-fns";
import { de, enGB, enUS, fr, nl } from "date-fns/locale";

export const DAY_MAPPING = {
  0: "SU",
  1: "MO",
  2: "TU",
  3: "WE",
  4: "TH",
  5: "FR",
  6: "SA",
} as const;

export function createWhatsAppLink(text: string) {
  // Encode text for URL and apply WhatsApp formatting
  const encodedText = encodeURIComponent(text)
    .replace(/\*([^*]+)\*/g, "*$1*") // bold: *text*
    .replace(/_([^_]+)_/g, "_$1_"); // italic: _text_

  return `whatsapp://send?text=${encodedText}`;
}

export function compareTimeOnly(date1: Date, date2: Date) {
  const time1 = setSeconds(
    new Date(0, 0, 0, date1.getHours(), date1.getMinutes(), date1.getSeconds()),
    0,
  );
  const time2 = setSeconds(
    new Date(0, 0, 0, date2.getHours(), date2.getMinutes(), date2.getSeconds()),
    0,
  );

  return isEqual(time1, time2);
}

export function convertSecondsToHoursAndMinutes(seconds: number) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  const hoursStr = hours.toString().padStart(2, "0");
  const minutesStr = minutes.toString().padStart(2, "0");

  if (hours > 0) {
    return `${hoursStr}:${minutesStr}`;
  } else {
    return `${minutes}`;
  }
}

export const delay = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export function highlightText(text?: string, highlightText?: string) {
  if (!highlightText || !text) return text;
  const regex = new RegExp(highlightText, "gi");
  return text.replace(
    regex,
    (match) => `<span class="bg-yellow-500">${match}</span>`,
  );
}

export const formatNewLineText = (text: string) =>
  text.replace(/\n/g, "<br />");

export function capitalize(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

export function getTextColor(bgColor?: string): string | undefined {
  if (!bgColor) return undefined;

  // Remove any leading '#' character from the text color if present
  const cleanColor = bgColor.replace("#", "");

  // Convert the text color to RGB values
  const r = parseInt(cleanColor.slice(0, 2), 16);
  const g = parseInt(cleanColor.slice(2, 4), 16);
  const b = parseInt(cleanColor.slice(4, 6), 16);

  // Calculate the brightness of the text color using a simple formula
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  // Determine the background color based on the brightness
  if (brightness > 127) {
    return "#000000"; // Dark background color for bright text
  } else {
    return "#FFFFFF"; // Light background color for dark text
  }
}

export const darkenColor = (
  factor: number,
  color?: string,
): string | undefined => {
  // Remove any leading '#' character from the color if present
  if (!color) return undefined;
  const cleanColor = color.replace("#", "");

  // Convert the color to RGB values
  let r = parseInt(cleanColor.slice(0, 2), 16);
  let g = parseInt(cleanColor.slice(2, 4), 16);
  let b = parseInt(cleanColor.slice(4, 6), 16);

  // Darken the color by reducing its brightness
  r = Math.round(r * (1 - factor));
  g = Math.round(g * (1 - factor));
  b = Math.round(b * (1 - factor));

  // Ensure the RGB values stay within the valid range
  r = Math.min(Math.max(r, 0), 255);
  g = Math.min(Math.max(g, 0), 255);
  b = Math.min(Math.max(b, 0), 255);

  // Convert the darkened RGB values back to hexadecimal
  const darkenedColor = `#${(r * 256 * 256 + g * 256 + b).toString(16).padStart(6, "0")}`;

  return darkenedColor;
};

export function formatIntToCurrency(
  input: number,
  showCurrency = true,
  locale = "nl",
) {
  return Intl.NumberFormat(locale, {
    style: showCurrency ? "currency" : "decimal",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(input);
}

export const generateRandomString = (length: number) => {
  let result = "";
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

export function groupBy<T, K1 extends keyof T, K2 extends keyof T>(
  array: T[] = [],
  key1: K1,
  key2?: K2,
): Record<string, T[]> {
  return array.reduce(
    (acc, item) => {
      const groupKey = key2
        ? `${String(item[key1])}-${String(item[key2])}`
        : String(item[key1]);
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(item);
      return acc;
    },
    {} as Record<string, T[]>,
  );
}

export function printLeftRight(left: string, right: string, width: number) {
  const space = " ";
  const w = width - left.toString().length - right.toString().length;
  for (let i = 0; i < w; i++) {
    left += " ";
  }
  left += right;
  return left;
}

export const getDateFNSLocale = (locale: string) => {
  switch (locale) {
    case "nl":
      return nl;
    case "de":
      return de;
    case "fr":
      return fr;
    default:
      return enGB;
  }
};

export const getSupportedLocales = () => {
  return [enGB, enUS, nl, fr, de];
};

export const isBetween = (
  currentDate: Date | string,
  startDate: Date | string,
  endDate: Date | string,
) => {
  const current = new Date(currentDate);
  const start = new Date(startDate);
  const end = new Date(endDate);

  return isSameOrAfter(current, start) && isSameOrBefore(current, end);
};

export const isSameOrBefore = (
  d1: Date | string = new Date(),
  d2: Date | string = new Date(),
) => {
  const date1 = new Date(d1);
  const date2 = new Date(d2);

  return isSameSecond(date1, date2) || isBefore(date1, date2);
};

export const isSameOrAfter = (
  d1: Date | string = new Date(),
  d2: Date | string = new Date(),
) => {
  const date1 = new Date(d1);
  const date2 = new Date(d2);

  return isSameSecond(date1, date2) || isAfter(date1, date2);
};

export function isSameTime(date1: Date, date2: Date): boolean {
  const hours1 = getHours(date1);
  const minutes1 = getMinutes(date1);

  const hours2 = getHours(date2);
  const minutes2 = getMinutes(date2);

  return hours1 === hours2 && minutes1 === minutes2;
}

export function isSameWeekDay(d1: Date | string, d2: Date | string): boolean {
  const date1 = new Date(d1);
  const date2 = new Date(d2);

  return date1.getDay() === date2.getDay();
}

export const nearestToMinute = (
  date: Date | string,
  minute: number,
  method: RoundingMethod = "round",
) =>
  roundToNearestMinutes(date, {
    nearestTo: minute,
    roundingMethod: method,
  });

export const nearestThirtyMinutes = (
  date: Date | string,
  method: RoundingMethod = "round",
) =>
  roundToNearestMinutes(date, {
    nearestTo: 30,
    roundingMethod: method,
  });

export * from "./constants";
